/**
 * Detects if an error is a chunk load failure (dynamic import 404).
 * This typically happens when:
 * - A new deployment is made while the user has the app open
 * - The old chunk file (e.g. dashboard-DxWxJDua.js) no longer exists
 * - Navigation triggers loading a lazy-loaded route chunk that returns 404
 *
 * @see https://vite.dev/guide/troubleshooting#handling-dynamic-import-errors
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message?.toLowerCase() ?? "";
  return (
    message.includes("failed to fetch dynamically imported module") ||
    message.includes("importing a module script failed") ||
    message.includes("loading chunk") ||
    message.includes("loading css chunk")
  );
}

/**
 * Registers a global handler for chunk load failures.
 * When a deployment happens mid-session, lazy-loaded route chunks can 404.
 * Reloading the page fetches fresh assets with correct chunk URLs.
 */
export function registerChunkLoadErrorHandler(): void {
  if (typeof window === "undefined") return;

  const handleChunkLoadError = (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault();
      window.location.reload();
    }
  };

  window.addEventListener("unhandledrejection", handleChunkLoadError);
}
